import React, { ChangeEvent, FunctionComponent, useState } from 'react';
import classnames from 'classnames';
import { searchIcon, successIcon } from '@jg/icons';
import Svg from '@jg/svg';
import Downshift from 'downshift';
import { useTranslation } from 'react-i18next';
import Alert from '@jg/alert';
import Loading from '@jg/loading';
import { useDebounce } from 'use-debounce';

import useOneSearch from '../../hooks/useOneSearch';

import './OneSearchPicker.scss';
import OneSearchPickerResult from './OneSearchPickerResult';
import { ICause } from '../../containers/SelectCausePage/SelectCausePage';
import { IEvent } from '../../containers/SelectEventPage/SelectEventPage';

type OneSearchPickerProps = {
  filterType: string;
  placeholder?: string;
  label: string;
  isLabelHidden?: boolean;
  onSelection: (selection: ICause & IEvent) => void;
  selected?: ICause | IEvent | null;
};

const OneSearchPicker: FunctionComponent<OneSearchPickerProps> = ({
  filterType,
  placeholder,
  label,
  isLabelHidden,
  onSelection,
  selected,
}) => {
  const { t } = useTranslation();
  const [searchValue, updateSearchValue] = useState('');
  const [debouncedSearchValue] = useDebounce(searchValue, 300);
  const { data, loading, error } = useOneSearch(
    debouncedSearchValue,
    filterType,
  );

  return (
    <Downshift
      onChange={selectedItem => {
        updateSearchValue('');
        onSelection(selectedItem);
      }}
      itemToString={item => (item ? item.value : searchValue)}
      selectedItem={selected}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getMenuProps,
        highlightedIndex,
        selectedItem,
        clearSelection,
      }) => (
        <div>
          {!selectedItem && (
            <div className="jg-form-group" data-qa="one-search-picker">
              <label
                data-qa="one-search-picker-label"
                {...getLabelProps({
                  className: classnames({
                    'jg-form-label': true,
                    'jg-sr-only': isLabelHidden,
                  }),
                })}
              >
                {label}
              </label>
              <div className="jg-position-relative">
                <div className="jg-tc-onesearch-picker__search__icon">
                  {loading === true && <Loading colour="purple" size="small" />}
                  {loading === false && (
                    <Svg
                      markup={searchIcon}
                      className="jg-icon jg-icon--medium-small jg-display-ib jg-fill--nobel"
                    />
                  )}
                </div>
                <input
                  {...getInputProps({
                    className:
                      'jg-form-control jg-tc-onesearch-picker__search__input',
                    'data-qa': 'onesearch-input',
                    placeholder,
                    value: searchValue,
                    onChange: (event: ChangeEvent<HTMLInputElement>) =>
                      updateSearchValue(event.target.value),
                  })}
                />
              </div>

              {data &&
                !error &&
                data.length > 0 && (
                  <ol
                    className="jg-tc-onesearch-picker__results"
                    data-qa="onesearch-results-list"
                    {...getMenuProps()}
                  >
                    {data.map((item, index) => (
                      <li
                        data-qa={`one-search-result-${index}`}
                        {...getItemProps({
                          key: `${item.type}_${
                            item.type === 'Campaign'
                              ? item.campaignGuid
                              : item.id
                          }`,
                          index,
                          item,
                          className: classnames({
                            'jg-tc-onesearch-picker__result': true,
                            'jg-tc-onesearch-picker__result--active':
                              highlightedIndex === index,
                          }),
                        })}
                      >
                        <OneSearchPickerResult {...item} />
                      </li>
                    ))}
                  </ol>
                )}

              {data &&
                !error &&
                data.length === 0 && (
                  <ol
                    className="jg-tc-onesearch-picker__results"
                    data-qa="one-search-noresults"
                  >
                    <li className="jg-tc-onesearch-picker__result">
                      {t('global.noresults')}
                    </li>
                  </ol>
                )}

              {error && (
                <Alert type="warning" className="jg-space-mvmd">
                  {t('global.error.fatal')}
                </Alert>
              )}
            </div>
          )}

          {selectedItem && (
            <>
              <div
                className="jg-tc-onesearch-picker__selected"
                data-qa="one-search-picker-selected"
              >
                <div className="jg-tc-onesearch-picker__selected__item">
                  <OneSearchPickerResult {...selectedItem} />
                </div>
                <div className="jg-tc-onesearch-picker__selected__icon">
                  <Svg
                    markup={successIcon}
                    className="jg-icon jg-icon--medium"
                  />
                </div>
              </div>
              <button
                className="jg-text--link jg-btn--invisible"
                type="button"
                onClick={() => {
                  clearSelection();
                }}
              >
                {t('global.change')}
              </button>
            </>
          )}
        </div>
      )}
    </Downshift>
  );
};

export default OneSearchPicker;
